import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./AdminForm.css";
import { API_BASE_URL, getSession } from "./apiConfig";

const PartyMaster = () => {
  const session = getSession();
  const userRole = session?.Role || "";

  const [activeTab, setActiveTab] = useState("entry");
  const [parties, setParties] = useState([]);
  const emptyFormState = { partyName: "", gstin: "", address: "", city: "", phone: "" };
  const [form, setForm] = useState(emptyFormState);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);

  const loadParties = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/party`);
      const data = await res.json();
      setParties(data);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load parties." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadParties(); }, []);

  const resetForm = () => {
    setForm(emptyFormState);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.partyName.trim()) {
      setMessage({ type: "error", text: "Party name is required." });
      return;
    }

    const isEditing = editingId !== null;
    const res = await fetch(
      isEditing ? `${API_BASE_URL}/party/${editingId}` : `${API_BASE_URL}/party`,
      {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }
    );

    if (res.ok) {
      setMessage({ type: "success", text: isEditing ? "Party updated successfully." : "Party added successfully." });
      resetForm();
      loadParties();
      setActiveTab("view");
    } else {
      setMessage({ type: "error", text: isEditing ? "Could not update party." : "Could not save party." });
    }
  };

  // `p` here comes from GET /api/party, which returns the raw EF entity
  // (partyId, partyName, gstin, address, city, phone).
  const handleEdit = (p) => {
    setForm({
      partyName: p.partyName || "",
      gstin: p.gstin || "",
      address: p.address || "",
      city: p.city || "",
      phone: p.phone || "",
    });
    setEditingId(p.partyId);
    setMessage({ type: "", text: "" });
    setActiveTab("entry");
  };

  const handleDelete = async (p) => {
    if (userRole !== "Admin") {
      setMessage({ type: "error", text: "Only Admin users can delete records." });
      return;
    }
    if (!window.confirm(`Delete party "${p.partyName}"?`)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/party/${p.partyId}`, {
        method: "DELETE",
        headers: { "X-User-Role": userRole },
      });
      if (!res.ok) {
        // 409 = blocked by the backend because this party is referenced
        // elsewhere (products, transactions, ledger entries)
        const text = await res.text().catch(() => "");
        throw new Error(text || "Delete failed");
      }
      setMessage({ type: "success", text: "Party deleted successfully." });
      loadParties();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Error deleting party." });
    }
  };

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Party Master</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            {editingId !== null ? "Edit Party" : "Create New Party"}
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({parties.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Party / Company Name *</label>
                  <input
                    className="form-control"
                    value={form.partyName}
                    onChange={(e) => setForm({ ...form, partyName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>GSTIN</label>
                  <input
                    className="form-control"
                    value={form.gstin}
                    onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    className="form-control"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    className="form-control"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    className="form-control"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit">
                {editingId !== null ? "Update Party" : "Add Party"}
              </button>
              {editingId !== null && (
                <button type="button" className="btn-secondary-sm" style={{ marginLeft: "10px" }} onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            {loading ? (
              <div className="empty-state">Loading records...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>GSTIN</th>
                    <th>City</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {parties.length > 0 ? (
                    parties.map((p) => (
                      <tr key={p.partyId}>
                        <td><strong>{p.partyName}</strong></td>
                        <td>{p.gstin || "-"}</td>
                        <td>{p.city || "-"}</td>
                        <td>{p.phone || "-"}</td>
                        <td>
                          <button className="btn-secondary-sm" onClick={() => handleEdit(p)}>Edit</button>
                          <button
                            className="btn-secondary-sm"
                            style={{ marginLeft: "6px", color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={() => handleDelete(p)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        <div style={{ marginBottom: "8px", fontSize: "1.2rem" }}>📋</div>
                        No parties found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PartyMaster;